import React from "react";
import {
	ArrayPick,
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	count,
	englishCommaJoinStrings,
	If,
	makeClassName,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { Badge } from "../../../../Components/Badge";
import { Icon } from "../../../../Components/Icon";
import {
	CourseSection,
	Employee,
	EmployeeNameFragment,
	HomeRoomSection,
	HomeRoomSectionNameFragment,
} from "../../../../Utilities";
import { CourseSectionFormat, EmployeeFormat, HomeRoomSectionFormat } from "../../../Format";
import { CourseSectionLink } from "../../../Links";
import { CourseSectionUtilities, StudentCourseSectionEnrollmentUtilities } from "../../../Utilities";
import * as ModelComponents from "../../_General";
import * as ClassYearComponents from "../../ClassYear";
import * as StudentComponents from "../../Student";
import styles from "./styles.module.css";

export type CourseSectionOverviewCardProps = {
	courseSection: Pick<CourseSection, "id" | "namePrefix" | "nameSuffix"> & {
		course: Pick<CourseSection["course"], "id" | "name"> & {
			academicSubject: Pick<CourseSection["course"]["academicSubject"], "color">;
			classYears: ArrayPick<CourseSection["course"]["classYears"], "id" | "name">;
		};
		homeRoomSection: (Pick<HomeRoomSection, "id"> & HomeRoomSectionNameFragment) | null;
		studentCourseSectionEnrollments: Pick<
			CourseSection["studentCourseSectionEnrollments"][0],
			"id" | "lateStartDate" | "earlyEndDate"
		>[];
	};
	withLessonPlans?: boolean;
};

export function CourseSectionOverviewCard({ courseSection, withLessonPlans = false }: CourseSectionOverviewCardProps) {
	const color = CourseSectionUtilities.getColor(courseSection);

	return (
		<Column
			className={styles["card"] + " " + styles["card--overview"]}
			style={{ "--course-section-color": color } as React.CSSProperties}
		>
			<CourseSectionLink instance={courseSection} className={styles["card__header"] + " link--no-text-decoration"}>
				<Column justify="spaced-start" verticalSpacing="0.625rem" className={styles["card__header__content"]}>
					<TruncatedText lines={2} className={styles["card__header__title"]}>
						{CourseSectionFormat.name(courseSection)}
					</TruncatedText>
					<Row justify="spaced-start" horizontalSpacing="0.625rem">
						<HomeRoomSectionOrClassYearsBadge courseSection={courseSection} />
						<StudentsBadge studentCourseSectionEnrollments={courseSection.studentCourseSectionEnrollments} />
					</Row>
				</Column>
				{/* TODO image/icon */}
			</CourseSectionLink>
			<If condition={withLessonPlans}>
				<Column justify="spaced-start" align="center" verticalSpacing="1rem" className={styles["card__section"]}>
					<strong>This Week</strong>
					<ModelComponents.CourseLessonPlanStatuses style={{ width: "100%" }} />
				</Column>
			</If>
			<Row justify="space-between" align="center" className={styles["card__footer"]}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.1875rem">
					<Button variant="link" size="small" onClick={() => {}}>
						<Icon.CalendarBlank size={24} />
					</Button>
					<Button variant="link" size="small" onClick={() => {}}>
						<Icon.Exam size={24} />
					</Button>
					<Button variant="link" size="small" onClick={() => {}}>
						<Icon.Folder size={24} />
					</Button>
				</Row>

				<Button variant="link" size="small" onClick={() => {}} style={{ fontSize: "1rem" }}>
					View Lesson Plan
				</Button>
			</Row>
		</Column>
	);
}

export type CourseSectionSimpleCardProps = {
	courseSection: Pick<CourseSection, "id" | "namePrefix" | "nameSuffix"> & {
		course: Pick<CourseSection["course"], "id" | "name"> & {
			academicSubject: Pick<CourseSection["course"]["academicSubject"], "id" | "name" | "color">;
			classYears: ArrayPick<CourseSection["course"]["classYears"], "id" | "name">;
		};
		homeRoomSection: (Pick<HomeRoomSection, "id"> & HomeRoomSectionNameFragment) | null;
		studentCourseSectionEnrollments: Pick<
			CourseSection["studentCourseSectionEnrollments"][0],
			"id" | "lateStartDate" | "earlyEndDate"
		>[];
		teacherCourseSectionEnrollments: { teacher: Pick<Employee, "id"> & EmployeeNameFragment }[];
	};
	slim?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function CourseSectionSimpleCard({
	courseSection,
	slim = false,
	className,
	style,
}: CourseSectionSimpleCardProps) {
	const color = CourseSectionUtilities.getColor(courseSection);

	const Layout = slim ? Row : Column;

	return (
		<Column
			className={makeClassName(styles["card"], slim ? styles["card--slim"] : styles["card--simple"], className)}
			style={{ "--course-section-color": color, ...style } as React.CSSProperties}
		>
			<div className={styles["card__header"] + " link--no-text-decoration"}>
				<Layout
					justify="space-between"
					align={slim ? "center" : undefined}
					horizontalSpacing="0.625rem"
					verticalSpacing="0.625rem"
					className={styles["card__header__content"]}
				>
					<TruncatedText lines={slim ? 1 : 2} className={styles["card__header__title"]}>
						{CourseSectionFormat.name(courseSection)}
					</TruncatedText>
					<If condition={!slim && courseSection.teacherCourseSectionEnrollments.length > 0}>
						<TruncatedText className={styles["card__header__teachers"]}>
							{englishCommaJoinStrings(
								courseSection.teacherCourseSectionEnrollments.map((e) => EmployeeFormat.name(e.teacher)),
							)}
						</TruncatedText>
					</If>
					<Row justify="spaced-start" horizontalSpacing="0.625rem" className={styles["card__badges"]}>
						<HomeRoomSectionOrClassYearsBadge courseSection={courseSection} slim={slim} />
						<StudentsBadge
							studentCourseSectionEnrollments={courseSection.studentCourseSectionEnrollments}
							slim={slim}
						/>
					</Row>
				</Layout>
				<If condition={!slim}>{/* TODO image/icon */}</If>
			</div>
		</Column>
	);
}

export type CourseSectionCustomCardProps = {
	courseSection: Omit<CourseSectionSimpleCardProps["courseSection"], "teacherCourseSectionEnrollments">;
} & Partial<ClassNameProps & StyleProps & ChildrenProps>;

export function CourseSectionCustomCard({ courseSection, className, style, children }: CourseSectionCustomCardProps) {
	const color = CourseSectionUtilities.getColor(courseSection);
	return (
		<Column
			className={makeClassName(styles["card"], styles["card--simple"], styles["card--custom"], className)}
			style={{ "--course-section-color": color, ...style } as React.CSSProperties}
		>
			<div className={styles["card__header"] + " link--no-text-decoration"}>
				<Column
					justify="space-between"
					horizontalSpacing="0.625rem"
					verticalSpacing="0.625rem"
					className={styles["card__header__content"]}
				>
					<TruncatedText className={styles["card__header__title"]}>
						{CourseSectionFormat.name(courseSection)}
					</TruncatedText>
					<Row justify="spaced-start" horizontalSpacing="0.625rem" className={styles["card__badges"]}>
						<HomeRoomSectionOrClassYearsBadge courseSection={courseSection} />
						<StudentsBadge studentCourseSectionEnrollments={courseSection.studentCourseSectionEnrollments} />
					</Row>
				</Column>
			</div>

			{children}
		</Column>
	);
}

export type HomeRoomSectionOrClassYearsBadgeProps = {
	courseSection: Pick<CourseSection, "id" | "namePrefix" | "nameSuffix"> & {
		course: Pick<CourseSection["course"], "id" | "name"> & {
			classYears: ArrayPick<CourseSection["course"]["classYears"], "id" | "name">;
		};
		homeRoomSection: (Pick<HomeRoomSection, "id"> & HomeRoomSectionNameFragment) | null;
	};
	slim?: boolean;
};

export function HomeRoomSectionOrClassYearsBadge({ courseSection, slim }: HomeRoomSectionOrClassYearsBadgeProps) {
	if (!!courseSection.homeRoomSection) {
		return (
			<Badge className={styles["card__badge"]}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<Icon.GraduationCap size={16} />
					<TruncatedText>
						{slim
							? HomeRoomSectionFormat.abbreviatedName(courseSection.homeRoomSection)
							: HomeRoomSectionFormat.name(courseSection.homeRoomSection)}
					</TruncatedText>
				</Row>
			</Badge>
		);
	}

	return (
		<ClassYearComponents.ClassYearsBadge
			classYears={courseSection.course.classYears}
			slim={slim}
			className={styles["card__badge"]}
		/>
	);
}

type StudentsBadgeProps = {
	studentCourseSectionEnrollments: CourseSectionOverviewCardProps["courseSection"]["studentCourseSectionEnrollments"];
	slim?: boolean;
};

function StudentsBadge({ studentCourseSectionEnrollments, slim }: StudentsBadgeProps) {
	const numActive = count(studentCourseSectionEnrollments, (e) => StudentCourseSectionEnrollmentUtilities.isActive(e));

	return (
		<StudentComponents.StudentsCountBadge
			count={numActive}
			slim={slim}
			className={styles["card__badge"] + " " + styles["card__badge--students"]}
		/>
	);
}
